import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Users, Route as RouteIcon,
  Truck, Cone, Bike, RefreshCw, Radar, Star,
} from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import HeroBackdrop from "../components/HeroBackdrop";
import { useContent } from "../context/ContentContext";

const APP_BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolvePhoto = (u) => (u && u.startsWith("/") ? `${APP_BACKEND}${u}` : u);
const teamInitials = (n) => (n || "?").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();

const IMG = {
  hero: "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/17pxmkkt_above-the-clouds-Medium.jpeg",
  collage: "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/jyt8bv4m_Skills-day-watching-closely-Medium.jpg",
  slow: "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/gu08wcps_skills-day-Medium.jpg",
  circuit: "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/iggs94a1_IPSGA-Medium.jpeg",
  hgv: "https://images.pexels.com/photos/13008065/pexels-photo-13008065.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  groupride: "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/uc3d0luq_Through-the-forest.jpeg",
  scenic1: "https://images.unsplash.com/photo-1690540293162-bfb097e68a34?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  scenic2: "https://images.unsplash.com/photo-1690540293148-affdded8f45a?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  scenic3: "https://images.unsplash.com/photo-1690540293130-2ca04fbf815b?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
};

const TIERS = [
  {
    key: "head", icon: ShieldCheck, title: "Head of Training",
    text: "Leads and supports the whole team, sets our standards and signs off every tutor's competence — so every rider is coached to the same high standard.",
    tag: "Head of Training",
  },
  {
    key: "tutor", icon: Users, title: "Advanced Tutors",
    text: "Our dedicated volunteer tutors — all RoSPA Advanced Tutors — who deliver your one-to-one, on-road coaching week in, week out and guide you all the way to your test.",
    tag: "RoSPA Advanced Tutor",
  },
];

const EXPERIENCE = [
  { icon: RouteIcon, title: "Observed rides", text: "Structured on-road rides with a tutor following, then debriefing your observation, positioning, progress and hazard management.", imgKey: "groupride", img: IMG.groupride },
  { icon: Bike, title: "One-to-one coaching", text: "Personal, patient coaching matched to your bike and your goals — from confident returners to seasoned tourers.", imgKey: "collage", img: IMG.collage },
  { icon: Cone, title: "Slow-riding & machine control days", text: "Low-speed control, U-turns, clutch/balance and manoeuvring drills in a safe, closed environment — the skills that build all-round confidence.", imgKey: "slow", img: IMG.slow },
  { icon: Truck, title: "Specialist awareness sessions", text: "From time to time we run extra sessions — such as large-vehicle awareness — to help you understand blind spots, swept paths and how to position yourself to be seen and stay safe around bigger vehicles.", imgKey: "hgv", img: IMG.hgv },
  { icon: Radar, title: "Group ride assessments", text: "Social, marshalled group rides that double as friendly assessments — great riding, great company, and useful feedback.", imgKey: "circuit", img: IMG.circuit },
  { icon: RefreshCw, title: "Continuous development", text: "Our tutors never stand still — they attend ongoing riding and training assessments so the coaching you receive is always current.", imgKey: "scenic1", img: IMG.scenic1 },
];

const ApplyButtons = ({ testid }) => (
  <div className="flex flex-col sm:flex-row gap-3" data-testid={testid}>
    <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold px-8">
      <Link to="/join" data-testid={`${testid}-apply`}>Start your application <ArrowRight className="h-4 w-4 ml-2" /></Link>
    </Button>
    <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white px-8">
      <Link to="/contact" data-testid={`${testid}-contact`}>Ask the training team</Link>
    </Button>
  </div>
);

export default function RiderTraining() {
  const { t } = useContent();
  const rt = (k, d) => t(`img.ridertraining.${k}`, d);
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/team", { params: { section: "training" } }).then(({ data }) => setTeam(data)).catch(() => {});
  }, []);
  return (
    <div data-testid="rider-training-page">
      {/* Hero */}
      <section className="relative bg-[#0F2744] overflow-hidden">
        <HeroBackdrop image={rt("hero", IMG.hero)} opacity={0.4} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold bg-amber-500 text-[#0F2744]">
            <Bike className="h-4 w-4" /> Riders · Training Team
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight max-w-3xl">
            Coached by the best, so you ride your best
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-2xl leading-relaxed">
            Our dedicated Training Team is made up entirely of volunteers, led by an experienced Head of Training, and every
            member has achieved the <strong>RoSPA Advanced Tutor</strong> qualification. They attend continuous
            riding and training assessments themselves — so the coaching you receive is always current, consistent
            and of the highest quality, whether you're a brand-new associate or a returning rider.
          </p>
          <div className="mt-8"><ApplyButtons testid="training-hero-apply" /></div>
        </div>
      </section>

      {/* Intro strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="eyebrow">Our promise</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744]">A team built around your progress</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Advanced riding isn't about being told off — it's about being coached up. Our volunteers give their time
            as a voluntary contribution because they love riding and love seeing others get safer and smoother. You'll always know where
            you stand, what to work on next, and exactly how it connects to the RoSPA advanced test.
          </p>
          <ul className="mt-6 space-y-3">
            {[
              "Led by an experienced Head of Training",
              "Every tutor holds the RoSPA Advanced Tutor qualification",
              "Continuous assessment keeps our standards high",
              "Patient, one-to-one coaching on your own machine",
            ].map((li) => (
              <li key={li} className="flex items-start gap-3 text-slate-700">
                <Star className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" /> <span>{li}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <img src={rt("circuit", IMG.circuit)} alt="RoSPA rider on a training day" className="rounded-2xl object-cover h-64 w-full col-span-2" data-testid="training-img-circuit" />
          <img src={rt("slow", IMG.slow)} alt="Slow-riding control practice" className="rounded-2xl object-cover h-44 w-full" data-testid="training-img-slow" />
          <img src={rt("groupride", IMG.groupride)} alt="Group ride in the countryside" className="rounded-2xl object-cover h-44 w-full" data-testid="training-img-group" />
        </div>
      </section>

      {/* Tutor structure */}
      <section className="bg-[#0F2744] py-20" data-testid="training-structure">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="eyebrow">Our team</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-white">A team of dedicated volunteers</h2>
          <p className="mt-4 text-slate-300 max-w-2xl">Everyone who coaches with us gives their time as a volunteer because they love riding and love helping others get safer and smoother. We're led by a Head of Training, and all of our tutors are qualified RoSPA Advanced Tutors.</p>
          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {TIERS.map((tier) => (
              <div key={tier.title} className="relative rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur" data-testid={`tier-${tier.key}`}>
                <div className="h-12 w-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
                  <tier.icon className="h-6 w-6 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-white">{tier.title}</h3>
                <p className="mt-1 text-xs font-semibold text-amber-400">{tier.tag}</p>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">{tier.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the team */}
      {team.length > 0 && (
        <section className="bg-slate-50 py-20" data-testid="training-people">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="eyebrow">Meet the team</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744]">The people you'll ride with</h2>
            {TIERS.map((tier) => {
              const people = team.filter((p) => p.tier === tier.key);
              if (!people.length) return null;
              return (
                <div key={tier.key} className="mt-10" data-testid={`people-tier-${tier.key}`}>
                  <h3 className="text-xl font-bold text-[#0F2744] flex items-center gap-2"><tier.icon className="h-5 w-5 text-amber-500" /> {tier.title}</h3>
                  <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {people.map((p) => (
                      <div key={p.id} className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid={`person-${p.id}`}>
                        <div className="aspect-[4/3] bg-slate-100 overflow-hidden flex items-center justify-center">
                          {p.photo ? <img src={resolvePhoto(p.photo)} alt={p.name} className="h-full w-full object-cover" />
                            : <div className="h-20 w-20 rounded-full bg-[#0F2744] text-white text-xl font-bold flex items-center justify-center">{teamInitials(p.name)}</div>}
                        </div>
                        <div className="p-5">
                          <div className="text-sm font-semibold text-amber-600">{p.role}</div>
                          <div className="mt-0.5 font-bold text-[#0F2744]">{p.name}</div>
                          {p.grade && <div className="mt-1 text-xs font-semibold text-slate-500">{p.grade}</div>}
                          {p.bio && <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.bio}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* What you'll experience */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="training-experience">
        <span className="eyebrow">What you'll experience</span>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744]">More than test prep — proper riding development</h2>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {EXPERIENCE.map((e, i) => (
            <div key={e.title} className="group rounded-2xl bg-white border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`experience-${i}`}>
              <div className="h-44 overflow-hidden">
                <img src={rt(e.imgKey, e.img)} alt={e.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2.5">
                  <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                    <e.icon className="h-5 w-5 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-[#0F2744]">{e.title}</h3>
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{e.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Photo band */}
      <section className="bg-slate-100 py-16" data-testid="training-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0F2744]">On a training day with us</h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            <img src={rt("scenic2", IMG.scenic2)} alt="Associates on a group ride" className="rounded-2xl object-cover h-56 w-full sm:col-span-2" />
            <img src={rt("scenic3", IMG.scenic3)} alt="Riders on a country road" className="rounded-2xl object-cover h-56 w-full" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#0F2744] overflow-hidden">
        <HeroBackdrop image={rt("circuit", IMG.circuit)} opacity={0.35} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl font-bold text-white">Ready to ride with our Training Team?</h2>
          <p className="mt-4 text-slate-200">Join as an associate and get matched with a RoSPA Advanced Tutor near you. It's {t("fee.join", "£65")} to join plus {t("fee.renewal", "£20")} annual club membership ({t("fee.join_plus_renewal", "£85")} today).</p>
          <div className="mt-8 flex justify-center"><ApplyButtons testid="training-cta-apply" /></div>
        </div>
      </section>
    </div>
  );
}
